<template>
  <div>
    <button class="submit-btn" v-on:click="authorizeKlarnaPayment" type="button">
      {{ t('klarnaWidget.authorizeButton.text') }}
    </button>
  </div>
</template>

<script>

import {mapGetters} from 'vuex'

export default {
  name: "KlarnaWidget",
  mounted() {
    window.klarnaAsyncCallback = this.initializeKlarna
    this.appendExternalKlarnaScript()
  },
  emits: ['authorize-klarna-payment-event'],
  methods: {
    ...mapGetters(['getSmartPointIsSelected', 'getSelectedSmartPoint']),
    appendExternalKlarnaScript() {
      if (!document.querySelector('script[src="https://x.klarnacdn.net/kp/lib/v1/api.js"]')) {
        const script = document.createElement('script')
        script.src = 'https://x.klarnacdn.net/kp/lib/v1/api.js'
        script.async = true
        document.head.appendChild(script)
      }
    },
    initializeKlarna() {
      if (!this.klarnaPaymentSessionComputed?.client_token) {
        return
      }

      Klarna.Payments.init({client_token: this.klarnaPaymentSessionComputed.client_token})
      Klarna.Payments.load({
        container: '#klarna-payment-container',
        payment_method_category: 'pay_now'
      }, function (res) {
        if (res.error) {
          console.error('Klarna error:', res.error)
        }
      })
    },
    explicitKlarnaPaymentsLoad() {
      Klarna.Payments.load({
          container: '#klarna-payment-container',
          payment_method_category: 'pay_now'
        },
        {
          purchase_country: this.$parent.orderCountry,
          purchase_currency: 'EUR',
          locale: 'el-GR',
          order_amount: Math.round(this.$parent.orderTotal),
          order_lines: this.generateOrderLines()
        }, function (res) {
          console.debug(res);
        })
    },
    authorizeKlarnaPayment() {
      const termsInput = document.querySelector('#termsacceptcheck')

      if (termsInput.checked === false) {
        alert(this.t('klarnaWidget.terms.required.text'))
        return
      }

      let params = this.generateAuthorizationParameters()
      Klarna.Payments.authorize(
        {},
        params,
        function (res) {
          if (res.show_form === false) {
            alert(this.t('klarnaWidget.authorization.missing'))
            return
          }

          this.$emit('authorize-klarna-payment-event', res)
        }.bind(this)
      )
    },
    generateAuthorizationParameters() {
      let authorizationParams = {billing_address: this.generateBillingAddress()}

      if (this.$parent.useAddress === this.$parent.orderAddressOptions.store) {
        authorizationParams.shipping_address = this.generateShippingAddressForStorePickup()
      } else if (this.$parent.useAddress === this.$parent.orderAddressOptions.shipping) {
        authorizationParams.shipping_address = this.generateShippingAddressForShipping()
      } else if (this.$parent.useAddress === this.$parent.orderAddressOptions.billing) {
        authorizationParams.shipping_address = authorizationParams.billing_address
      }

      let attachment = {}

      if (this.previewOrderData.customerData.is_guest === '0') {
        attachment.body = {
          customer_account_info: [{
            unique_account_identifier: this.previewOrderData.customerData.id,
          }]
        }
      }

      if (this.$parent.useAddress === this.$parent.orderAddressOptions.store) {
        if (!attachment.hasOwnProperty('body')) {
          attachment.body = {}
        }

        attachment.body.other_delivery_address = this.generateAttachmentOtherDeliveryAddressForStorePickup()
      }

      if (this.getSmartPointIsSelected()) {
        if (!attachment.hasOwnProperty('body')) {
          attachment.body = {}
        }

        attachment.body.other_delivery_address = this.generateAttachmentOtherDeliveryAddressSmartPoint()
      }

      if (Object.keys(attachment).length > 0) {
        authorizationParams.attachment = {
          content_type: 'application/vnd.klarna.internal.emd-v2+json',
          body: JSON.stringify(attachment.body)
        }
      }

      return authorizationParams
    },
    generateBillingAddress() {
      return {
        given_name: this.cleanString(this.previewOrderData.customerData.name),
        family_name: this.cleanString(this.previewOrderData.customerData.surname),
        email: this.previewOrderData.customerData.mail,
        street_address: this.previewOrderData.customerData.address,
        postal_code: this.previewOrderData.customerData.postal,
        city: this.previewOrderData.customerData.city,
        phone: this.previewOrderData.customerData.landphone,
        country: this.previewOrderData.customerData.country
      }
    },
    generateShippingAddressForStorePickup() {
      let stores = this.previewOrderData.activeStores
      let store

      if (this.previewOrderData.activeStores.length === 1) {
        store = stores.find(store => store.id === this.previewOrderData.activeStores[0].id)
      } else {
        store = stores.find(store => store.id === this.previewOrderData.activeStores.storeId)
      }

      return {
        given_name: this.cleanString(this.previewOrderData.customerData.name),
        family_name: this.cleanString(this.previewOrderData.customerData.surname),
        email: this.previewOrderData.customerData.mail,
        street_address: store.address,
        postal_code: store.postal,
        city: store.city,
        phone: store.phone,
        country: store.country
      }
    },
    generateShippingAddressForShipping() {
      if (this.getSmartPointIsSelected) {
        return {
          given_name: this.cleanString(this.previewOrderData.customerData.sendto_name),
          family_name: this.cleanString(this.previewOrderData.customerData.sendto_surname),
          email: this.previewOrderData.customerData.mail,
          street_address: this.getSelectedSmartPoint.address,
          postal_code: this.getSelectedSmartPoint.postalCode
        }
      } else {
        return {
          given_name: this.cleanString(this.previewOrderData.customerData.sendto_name),
          family_name: this.cleanString(this.previewOrderData.customerData.sendto_surname),
          email: this.previewOrderData.customerData.mail,
          street_address: this.previewOrderData.customerData.sendto_address,
          postal_code: this.previewOrderData.customerData.sendto_postal,
          city: this.previewOrderData.customerData.sendto_city,
          phone: this.previewOrderData.customerData.sendto_landphone,
          country: this.previewOrderData.customerData.sendto_country
        }
      }
    },
    generateAttachmentOtherDeliveryAddressForStorePickup() {
      let stores = this.previewOrderData.activeStores
      let store

      if (this.previewOrderData.activeStores.length === 1) {
        store = stores.find(store => store.id === this.previewOrderData.activeStores[0].id)
      } else {
        store = stores.find(store => store.id === this.previewOrderData.activeStores.storeId)
      }

      return [{
        shipping_method: 'store pick-up',
        shipping_type: "express",
        first_name: this.cleanString(this.previewOrderData.customerData.name),
        last_name: this.cleanString(this.previewOrderData.customerData.surname),
        street_address: store.address,
        postal_code: store.postal,
        city: store.city,
        country: store.country
      }]
    },
    generateAttachmentOtherDeliveryAddressSmartPoint() {
      return [{
        shipping_method: 'store pick-up',
        shipping_type: "express",
        first_name: this.cleanString(this.previewOrderData.customerData.name),
        last_name: this.cleanString(this.previewOrderData.customerData.surname),
        street_address: this.getSelectedSmartPoint.address,
        postal_code: this.getSelectedSmartPoint.postalCode
      }]
    },
    generateOrderLines() {
      let cart = []
      for (const item of this.cartContents) {
        let cartItem = this.getCartProductById(item.productId)
        let klarnaOrderLine = {
          type: "physical",
          reference: item.productCodeId,
          name: cartItem.productName,
          quantity: item.quantity,
          unit_price: Math.round((cartItem.productOldPrice * 100)),
          total_amount: Math.round((cartItem.productFinalPrice * item.quantity * 100)),
        }

        if (cartItem.productHasDiscount) {
          let discountAmount = cartItem.productOldPrice - cartItem.productFinalPrice;
          klarnaOrderLine.total_discount_amount = Math.round((discountAmount * item.quantity * 100))
        }

        cart.push(klarnaOrderLine)
      }

      if (this.$parent.transportationCost > 0) {
        cart.push({
          type: 'shipping_fee',
          name: 'Shipping',
          quantity: 1,
          unit_price: Math.round(this.$parent.transportationCost * 100),
          total_amount: Math.round(this.$parent.transportationCost * 100)
        })
      }

      if (this.$parent.couponDiscount > 0) {
        cart.push({
          type: 'physical',
          name: 'coupon discount',
          quantity: 1,
          unit_price: -Math.abs(Math.round(this.$parent.couponDiscount * 100)),
          total_amount: -Math.abs(Math.round(this.$parent.couponDiscount * 100))
        })
      }

      if (this.$parent.customerPointsRedeemValue > 0) {
        cart.push({
          type: 'physical',
          name: 'discount from points deduction',
          quantity: 1,
          unit_price: -Math.abs(Math.round(this.$parent.customerPointsRedeemValue * 100)),
          total_amount: -Math.abs(Math.round(this.$parent.customerPointsRedeemValue * 100))
        })
      }

      if (this.$parent.giftPackagingCost > 0) {
        cart.push({
          type: 'physical',
          name: 'gift packaging',
          quantity: 1,
          unit_price: Math.round(this.$parent.giftPackagingCost * 100),
          total_amount: Math.round(this.$parent.giftPackagingCost * 100)
        })
      }

      return cart
    },
    cleanString(string) {
      return string.normalize('NFD').toUpperCase().replace(/[\u0300-\u036f]/g, "")
    }
  },
  computed: {
    ...mapGetters(['previewOrderData', 'cartContents', 'getCartProductById']),
    klarnaPaymentSessionComputed() {
      return this.$store.state.klarnaPaymentSession
    }
  },
  watch: {
    klarnaPaymentSessionComputed: {
      handler(value) {
        if (value === null) {
          return
        }

        if (value.hasOwnProperty('client_token')) {
          this.initializeKlarna()
        }
      },
      deep: true
    }
  }
}
</script>
